import React from 'react';
import { Link } from 'react-router-dom';
import { CheckIcon, XIcon } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { TryFreeLink } from '../components/TryFreeLink';
import { pricing, maxProfilesWord } from '../lib/pricing';
const steps = [{
  title: 'Set up a profile',
  body: `A first name or nickname and one of eight animal and robot buddies your child picks. Up to ${maxProfilesWord} profiles on one subscription.`
}, {
  title: 'Pick a world, pick an adventure',
  body: `${pricing.worldCount} worlds, ten adventures each. Your child follows whatever looks interesting.`
}, {
  title: 'Read or listen',
  body: 'One short idea per card, with an illustration drawn for that sentence. Roughly five to eight minutes.'
}, {
  title: 'Try a question, earn a badge',
  body: 'One question at the end, then a named badge to keep and download, with a grown-up’s help to save or share it.'
}];
const has = ['Parent-protected settings and subscription options', `Separate profiles for up to ${maxProfilesWord} children`, 'Saved progress for each profile', 'Adventures that come to a natural end'];
const hasNot = ['Ads', 'Scores, streaks or leaderboards', 'Chat or messaging with other users', 'Content posted by other users'];
export function Parents() {
  return <>
      <PageHeader title="For parents" intro="What the app does, what it deliberately leaves out, and what you can manage from the parent area." data-id="element-397" data-mp="3sq5evmz2qs45" />

      <section aria-labelledby="how-it-works" className="bg-cream" data-id="element-398" data-mp="300ooeuv0llak">
        <div className="mx-auto max-w-content px-5 py-16 sm:px-6 md:py-20" data-id="element-399" data-mp="3ewo34ocmqjoo">
          <h2 id="how-it-works" className="font-display text-[32px] tracking-tight text-ink md:text-[40px]" data-id="element-400" data-mp="39xkxb1ots9fn">
            How it works
          </h2>
          <ol className="mt-10 grid gap-10 md:grid-cols-2 lg:grid-cols-4 lg:gap-8" data-id="element-401" data-mp="u4m1nkv83zcl">
            {steps.map((step, i) => <li key={step.title} data-id="element-402" data-mp="p9f28p8zlgth" data-mpi={i}>
                <span className="flex h-11 w-11 items-center justify-center rounded-full border-2 border-ink bg-sun font-display text-[20px] text-ink" data-id="element-403" data-mp="wasfk356lkvy">
                  {i + 1}
                </span>
                <h3 className="mt-4 font-display text-[21px] leading-snug text-ink" data-id="element-404" data-mp="1686svtvt5tk1">{step.title}</h3>
                <p className="mt-2 text-[17px] text-ink-soft" data-id="element-405" data-mp="1b7t5c13tb5rs">{step.body}</p>
              </li>)}
          </ol>
        </div>
      </section>

      <section aria-labelledby="safety" className="bg-white" data-id="element-406" data-mp="dhohm5kxklvj">
        <div className="mx-auto max-w-content px-5 py-16 sm:px-6 md:py-20" data-id="element-407" data-mp="ijx8mwe8tdv0">
          <h2 id="safety" className="font-display text-[32px] tracking-tight text-ink md:text-[40px]" data-id="element-408" data-mp="nhfay3xr8uu1">
            Safety and privacy
          </h2>
          <div className="mt-10 grid gap-6 md:grid-cols-2" data-id="element-409" data-mp="sa03l24xd1vz">
            <div className="rounded-3xl border-2 border-ink bg-lavender-soft p-7" data-id="element-410" data-mp="3qzgxzkbsmc4s">
              <h3 className="font-display text-[21px] text-ink" data-id="element-411" data-mp="3vx8qogk9i2a1">What is in the app</h3>
              <ul className="mt-5 space-y-3" data-id="element-412" data-mp="2d2e22j5uers2">
                {has.map(item => <li key={item} className="flex gap-3 text-[17px] text-ink" data-id="element-413" data-mp="2d0bavvsyeslr">
                    <CheckIcon className="mt-1 h-5 w-5 shrink-0 text-teal" strokeWidth={2.75} aria-hidden="true" data-id="element-414" data-mp="291u7nncnm29m" />
                    {item}
                  </li>)}
              </ul>
            </div>
            <div className="rounded-3xl border-2 border-ink bg-cream p-7" data-id="element-415" data-mp="1z58weka1juxc">
              <h3 className="font-display text-[21px] text-ink" data-id="element-416" data-mp="1u534d2zv06eq">What is not</h3>
              <ul className="mt-5 space-y-3" data-id="element-417" data-mp="1pc9z8e0haw24">
                {hasNot.map(item => <li key={item} className="flex gap-3 text-[17px] text-ink" data-id="element-418" data-mp="1lcjaefuiiq08">
                    <XIcon className="mt-1 h-5 w-5 shrink-0 text-coral-dark" strokeWidth={2.75} aria-hidden="true" data-id="element-419" data-mp="1gcwxy8middsh" />
                    {item}
                  </li>)}
              </ul>
            </div>
          </div>
          <p className="mt-8 max-w-2xl text-[17px] text-ink-soft" data-id="element-420" data-mp="16eqydxo6cjqs">
            Profiles can include a nickname, buddy and interests, plus lesson progress, answers or quiz results, measured
            lesson time, earned badges and activity records. Saved profiles can also contain a legacy daily learning-goal
            value, although current child setup does not ask for it. We do not ask for a child’s email address, age range
            or reading comfort in child setup. Our{' '}
            <Link to="/privacy" className="font-bold text-ink underline decoration-coral decoration-2 underline-offset-4" data-id="element-421" data-mp="2np61hszanv6g">
              privacy policy
            </Link>{' '}
            sets out the full detail.
          </p>
        </div>
      </section>

      <section aria-labelledby="dashboard" className="bg-lavender-soft" data-id="element-422" data-mp="2iqw2t8ed2cg3">
        <div className="mx-auto max-w-content px-5 py-16 sm:px-6 md:py-20" data-id="element-423" data-mp="2isytzvr92bme">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16" data-id="element-424" data-mp="c0y1kv09i50v">
            <div data-id="element-425" data-mp="h014o9wom167">
              <h2 id="dashboard" className="font-display text-[32px] tracking-tight text-ink md:text-[40px]" data-id="element-426" data-mp="gyg9qj6ggg7t">
                The parent area
              </h2>
              <p className="mt-4 text-[18px] text-ink-soft" data-id="element-427" data-mp="1wiqqtxmkhsxt">
                Protected by a parent check. A short, readable summary, not an analytics dashboard.
              </p>
              <TryFreeLink className="mt-8" data-id="element-428" data-mp="2b96d7fqpz6xk" />
            </div>

            <div className="rounded-4xl border-2 border-ink bg-white p-7 shadow-chunky" data-id="element-429" data-mp="26fv4f92ckg30">
              <dl className="divide-y-2 divide-ink/10" data-id="element-430" data-mp="2k709wrsec2l9">
                {[['Adventures finished', 'Per child, with the world it came from'], ['Badges earned', 'Each child’s own named collection'], ['Profiles', `Add, rename or remove up to ${maxProfilesWord} children`], ['Subscription', 'Your plan, and how to cancel in Apple settings']].map(([label, value]) => <div key={label} className="flex flex-wrap items-baseline justify-between gap-2 py-4" data-id="element-431" data-mp="ut3ht57ycvy7">
                    <dt className="font-display text-[18px] text-ink" data-id="element-432" data-mp="2uvmhefc0t3mb">{label}</dt>
                    <dd className="text-[16px] text-ink-soft" data-id="element-433" data-mp="2uxpjhxqg6n7e">{value}</dd>
                  </div>)}
              </dl>
            </div>
          </div>
        </div>
      </section>
    </>;
}